import { bisectCenter } from 'd3-array';
import React, { useCallback, useMemo } from 'react';

import { NumberCoercible } from '../../utils/NumberCoercible';
import { NumberScale } from '../../utils/NumberScale';
import { TimeScale } from '../../utils/TimeScale';

export interface InteractivePointEvent<TValueType> {
  /**
   * The original mouse event associated with the user's action.
   */
  domEvent: React.MouseEvent;
  /**
   * The original value associated with the point that was targeted.
   */
  activePoint: TValueType;
}

export type InteractivePointEventHandler<TValueType> = (e: InteractivePointEvent<TValueType>) => void;

interface InteractiveOverlayProps<TValueType> {
  /**
   * The values to use to determine the point positions. This array must be sorted in ascending
   * order with respect to the x-value (first element).
   */
  data: [x: NumberCoercible, yValues: TValueType[]][];
  /**
   * Handler to invoke when a point is clicked.
   */
  onClick: InteractivePointEventHandler<TValueType> | undefined;
  /**
   * Handler to invoke when the user hovers over (or near) a point.
   */
  onHoverPointChange: InteractivePointEventHandler<TValueType | undefined>;
  /**
   * The x-scale used to position each point.
   */
  xScale: NumberScale | TimeScale;
  /**
   * A function to return the y-coordinate for a point. If the point should be skipped, this
   * function should return `undefined`.
   */
  yGetter: (d: TValueType) => NumberCoercible;
  /**
   * The y-scale used to position each point.
   */
  yScale: NumberScale | TimeScale;
}

/**
 * Padding to add around the overlay. This allows the mouse to be tracked beyond the immediate
 * boundaries of the plot, which could help users select points that are on or near the edge of the
 * plot.
 */
const OVERLAY_PADDING_PX = 8;

const InteractiveOverlayInner = <TValueType extends unknown>({
  data,
  onClick,
  onHoverPointChange,
  xScale,
  yGetter,
  yScale,
}: InteractiveOverlayProps<TValueType>): JSX.Element => {
  const [xMin, xMax] = xScale.range();
  const [yMax, yMin] = yScale.range();

  const xValues = useMemo(() => data.map(([k]) => k), [data]);

  const getNearestPoint = useCallback(
    (e: React.PointerEvent<SVGRectElement> | React.MouseEvent<SVGRectElement>): TValueType | undefined => {
      if (data.length === 0) {
        return undefined;
      }

      const { clientX, clientY } = e;

      const target = e.target as SVGRectElement;
      const x = clientX - target.clientLeft - target.getBoundingClientRect().left - OVERLAY_PADDING_PX + xMin;
      const y = clientY - target.clientTop - target.getBoundingClientRect().top - OVERLAY_PADDING_PX + yMin;

      const invertedX = xScale.invert(x);
      const invertedY = yScale.invert(y);

      // Type assertions below are necessary because bisectCenter's overloads are not generic
      const nearestXIndex = bisectCenter(xValues as ArrayLike<number>, invertedX as number);

      const candidatePoints = data[nearestXIndex][1]
        .map((point, index) => ({ point, index }))
        .sort((a, b) => {
          const aY = yGetter(a.point);
          const bY = yGetter(b.point);
          const diff = aY.valueOf() - bY.valueOf();

          if (diff !== 0) {
            return diff;
          }

          // If the values are equivalent, preserve order by index. This ensures the expected stack
          // is selected when dealing with stacked data with a value of 0 at a particular point.
          return a.index - b.index;
        });
      const yValues = candidatePoints.map((candidate) => yGetter(candidate.point));

      // Type assertions below are necessary because bisectCenter's overloads are not generic
      const nearestPointIndex = bisectCenter(yValues as ArrayLike<number>, invertedY as number);
      const nearestPoint = candidatePoints[nearestPointIndex].point;

      // TODO: measure the distance between the mouse and the rendered nearest point, and return
      // undefined if the distance is greater than some (configurable?) threshold.

      return nearestPoint;
    },
    [data, xMin, xScale, xValues, yGetter, yMin, yScale],
  );

  const handlePointerActivity = useCallback<React.PointerEventHandler<SVGRectElement>>(
    (e) => {
      const nearestPoint = getNearestPoint(e);
      onHoverPointChange({ activePoint: nearestPoint, domEvent: e });
    },
    [getNearestPoint, onHoverPointChange],
  );

  const handlePointerOut = useCallback<React.PointerEventHandler<SVGRectElement>>(
    (e) => {
      onHoverPointChange({ activePoint: undefined, domEvent: e });
    },
    [onHoverPointChange],
  );

  const handleClick = useCallback<React.MouseEventHandler<SVGRectElement>>(
    (e) => {
      if (onClick) {
        const nearestPoint = getNearestPoint(e);
        if (nearestPoint) {
          onClick({ activePoint: nearestPoint, domEvent: e });
        }
      }
    },
    [getNearestPoint, onClick],
  );

  const height = yMax - yMin;
  const width = xMax - xMin;

  // avoid rendering a rect with invalid dimensions
  if (height <= 0 || width <= 0) {
    return null as unknown as JSX.Element;
  }

  return (
    <rect
      fill="transparent"
      x={xMin - OVERLAY_PADDING_PX}
      y={yMin - OVERLAY_PADDING_PX}
      height={height + OVERLAY_PADDING_PX * 2}
      width={width + OVERLAY_PADDING_PX * 2}
      onPointerOver={handlePointerActivity}
      onPointerMove={handlePointerActivity}
      onPointerOut={handlePointerOut}
      onClick={handleClick}
      cursor={onClick ? 'pointer' : undefined}
    />
  );
};

export const InteractiveOverlay = React.memo(InteractiveOverlayInner) as typeof InteractiveOverlayInner;
